import { FormEvent, useContext, useState } from "react";

import { TemplateStepConstants } from "../../../../../../../../../../../constants";
import { ServiceError, Status } from "../../../../../../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../../../../../../service/ValidationErrorFormatter";
import { addOrRemoveWorkflowStep } from "../../../../../../../../../../../service/workflow";
import { useIsLoadingWrapper } from "../../../../../../../../../../../utils";
import { Toast } from "../../../../../../../../../../../widget";
import { TasksBoardContext } from "../../../../../../TasksBoardContext";

interface UseDeleteStepModalProps {
  closeModal: () => void;
  workflowUuid?: string;
  workflowStepUuid?: string;
  workflowStepRowVersion: number;
}

interface UseDeleteStepModalReturnData {
  handleDeleteStep: (e: FormEvent<HTMLFormElement>) => void;
  isHandleDeleteStepLoading: boolean;
  onCancel: () => void;
  errors: ServiceError[];
}

export const useDeleteStepModal = ({
  closeModal,
  workflowUuid,
  workflowStepUuid,
  workflowStepRowVersion,
}: UseDeleteStepModalProps): UseDeleteStepModalReturnData => {
  const { shouldRefreshTasksBoard } = useContext(TasksBoardContext);
  const [isHandleDeleteStepLoading, setIsHandleDeleteStepLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  const onCancel = (): void => {
    setErrors([]);
    closeModal();
  };

  const handleDeleteStep = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (!workflowStepUuid) {
      setErrors([
        {
          code: "NO_WORKFLOW_STEP",
          message: "A workflow step must be selected",
        },
      ]);
    } else if (!workflowUuid) {
      setErrors([
        {
          code: "NO_WORKFLOW",
          message: "A workflow must be selected",
        },
      ]);
    } else {
      await addOrRemoveWorkflowStep({
        workflowUuid,
        workflowStepUuid,
        workflowStepRowVersion,
        operation: TemplateStepConstants.REMOVE_OPERATION,
        isRepeatable: null,
        phase: null,
        stepName: null,
      }).then((response) => {
        if (response.status === Status.Success) {
          Toast.success({ message: "Workflow step successfully deleted" });

          shouldRefreshTasksBoard();
          onCancel();
        }
        if (response.status === Status.Error && response.errors) {
          setErrors(response.errors.map((error) => ({ ...error, message: getErrorMessageFromCode(error.code) })));
        }
      });
    }
  }, setIsHandleDeleteStepLoading);

  return { isHandleDeleteStepLoading, handleDeleteStep, onCancel, errors };
};
